import { Link } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, Coins, User } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { useCart } from "../lib/CartContext";
import { usePirate, formatBerry } from "../lib/PirateContext";
import { useCustomer } from "../lib/CustomerContext";
import { JollyRoger } from "./icons/PirateIcons";
import { THEMED_CATEGORIES } from "../lib/constants";
import SearchModal from "./SearchModal";

export { THEMED_CATEGORIES };

export default function Navbar() {
    const { count, setOpen } = useCart();
    const { berry, level } = usePirate();
    const { isAuthed, customer } = useCustomer();
    const [menu, setMenu] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    return (
        <header
            className="sticky top-0 z-40 bg-parchment border-b-2 border-ink"
            data-testid="main-navbar"
        >
            {/* Top ribbon */}
            <div className="ribbon-ticker text-xs font-mono uppercase tracking-widest">
                <div className="scroll-marquee animate-marquee">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex gap-12 px-6 shrink-0"
                        >
                            <span>★ Spedizione gratuita oltre €120</span>
                            <span>⚓ Iscriviti alla ciurma: 100 XP + 500 Berry</span>
                            <span>☠ Edizioni Esclusivo limitate</span>
                            <span>🗺 Consegna in tutto il Grand Line</span>
                            <span>💰 1000 Berry = 1€ di sconto</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="logo-home-link"
                    className="flex items-center gap-3 group"
                >
                    <motion.div
                        whileHover={{ rotate: -18, scale: 1.05 }}
                        className="w-11 h-11 bg-bounty text-parchment border-2 border-ink shadow-manga flex items-center justify-center rotate-stamp"
                    >
                        <JollyRoger size={26} className="text-parchment" />
                    </motion.div>
                    <div className="leading-none">
                        <div className="font-display text-3xl tracking-tight">
                            DESASHOP
                        </div>
                        <div className="font-hand text-xs text-ink/70 -mt-1">
                            il bottino dei pirati
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8 font-display text-lg tracking-wider">
                    {THEMED_CATEGORIES.map((c) => (
                        <Link
                            key={c.slug}
                            to={`/figure${c.param}`}
                            className="relative group py-1"
                            data-testid={`nav-${c.slug}`}
                        >
                            <span className="group-hover:text-bounty transition-manga">
                                {c.label}
                            </span>
                            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-bounty scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {/* Berry pill (compact) */}
                    <div
                        className="hidden md:flex items-center gap-1.5 px-3 h-11 border-2 border-ink bg-gold shadow-manga font-mono text-sm font-bold"
                        title={`Livello ${level.level} · ${level.name}`}
                        data-testid="nav-berry-pill"
                    >
                        <Coins size={14} />
                        {formatBerry(berry)}
                    </div>

                    <button
                        type="button"
                        className="hidden md:inline-flex h-11 w-11 items-center justify-center border-2 border-ink bg-white shadow-manga hover:shadow-manga-lg transition-manga"
                        aria-label="Cerca"
                        onClick={() => setSearchOpen(true)}
                        data-testid="search-btn"
                    >
                        <Search size={18} />
                    </button>
                    <Link
                        to={isAuthed ? "/account" : "/account/login"}
                        className="hidden md:inline-flex h-11 w-11 items-center justify-center border-2 border-ink bg-white shadow-manga hover:shadow-manga-lg transition-manga relative"
                        aria-label={isAuthed ? "Il mio account" : "Accedi"}
                        title={isAuthed ? customer?.displayName || "Account" : "Accedi"}
                        data-testid="account-btn"
                    >
                        <User size={18} />
                        {isAuthed && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-bounty border-2 border-parchment" />
                        )}
                    </Link>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="relative h-11 px-4 inline-flex items-center gap-2 border-2 border-ink bg-bounty text-parchment shadow-manga hover:shadow-manga-lg transition-manga"
                        data-testid="cart-open-btn"
                    >
                        <ShoppingBag size={18} />
                        <span className="font-display tracking-widest hidden sm:inline">
                            BOTTINO
                        </span>
                        {count > 0 && (
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute -top-2 -right-2 bg-ink text-parchment text-xs font-mono w-6 h-6 flex items-center justify-center border-2 border-parchment"
                                data-testid="cart-count"
                            >
                                {count}
                            </motion.span>
                        )}
                    </button>
                    <button
                        type="button"
                        className="lg:hidden h-11 w-11 inline-flex items-center justify-center border-2 border-ink bg-white shadow-manga"
                        onClick={() => setMenu(!menu)}
                        aria-label="Menu"
                        data-testid="mobile-menu-btn"
                    >
                        {menu ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {menu && (
                <div className="lg:hidden border-t-2 border-ink bg-parchment">
                    <div className="px-5 py-4 flex flex-col gap-3">
                        <button
                            onClick={() => {
                                setMenu(false);
                                setSearchOpen(true);
                            }}
                            className="border-2 border-ink px-4 py-3 shadow-manga bg-white flex items-center gap-3 hover:bg-seafoam"
                            data-testid="mobile-search-btn"
                        >
                            <Search size={18} />
                            <span className="font-display text-xl tracking-wider">
                                CERCA UN PIRATA
                            </span>
                        </button>
                        {THEMED_CATEGORIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/figure${c.param}`}
                                onClick={() => setMenu(false)}
                                className="border-2 border-ink px-4 py-3 shadow-manga bg-white flex items-center justify-between hover:bg-seafoam"
                                data-testid={`mobile-nav-${c.slug}`}
                            >
                                <div>
                                    <div className="font-display text-2xl tracking-wider">
                                        {c.label}
                                    </div>
                                    <div className="font-hand text-sm text-ink/70">
                                        {c.subtitle}
                                    </div>
                                </div>
                                <span className="font-hand text-bounty text-2xl">
                                    →
                                </span>
                            </Link>
                        ))}
                        <Link
                            to="/figure"
                            onClick={() => setMenu(false)}
                            className="text-center font-mono text-xs uppercase tracking-widest py-2"
                        >
                            vedi tutte le figure
                        </Link>
                    </div>
                </div>
            )}

            <SearchModal
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
            />
        </header>
    );
}
